import { Controller } from "@hotwired/stimulus"

// A <details> disclosure whose body is fetched the first time it is opened, so
// a page of cards doesn't pull every preview up front.
//
// Usage:
//   <details data-controller="lazy-details"
//            data-lazy-details-url-value="/some/fragment"
//            data-action="toggle->lazy-details#toggle">
//     <summary>Fabric mocks</summary>
//     <div data-lazy-details-target="body">Loading…</div>
//   </details>
//
// The fragment may contain links with data-action="click->lazy-details#more"
// (e.g. "Show all"); they reload the body from the link's own href.
//
// Targets:
//   body — where the fetched HTML goes.
// Values:
//   url    — the fragment to fetch on first open.
//   loaded — set once a fetch has succeeded, so re-opening doesn't refetch.
export default class extends Controller {
  static targets = ["body"]
  static values  = { url: String, loaded: Boolean }

  toggle() {
    if (this.element.open && !this.loadedValue) this.load(this.urlValue)
  }

  more(event) {
    event.preventDefault()
    this.load(event.currentTarget.href)
  }

  retry() {
    this.load(this.urlValue)
  }

  async load(url) {
    this.bodyTarget.setAttribute("aria-busy", "true")
    this.bodyTarget.innerHTML = '<p class="text-xs text-stone-400 py-2" role="status">Loading…</p>'

    try {
      const response = await fetch(url, {
        headers:     { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      this.bodyTarget.innerHTML = await response.text()
      this.loadedValue = true
    } catch (error) {
      this.bodyTarget.innerHTML =
        '<p class="text-xs text-red-600 py-2" role="alert">Couldn\'t load these. ' +
        '<button type="button" class="underline" data-action="lazy-details#retry">Try again</button></p>'
    } finally {
      this.bodyTarget.removeAttribute("aria-busy")
    }
  }
}
